import { useState, useEffect } from "react";
import { TIER_ORDER, TIER_COLORS } from "../utils/tiers";
import { getGroupSchedule, getEndTime } from "../utils/time";
import PlayerCard from "../components/PlayerCard";
import GroupModal from "../components/GroupModal";
import { getWarnings, hasAvailabilityConflict, hasUnmetPartnerPreference, getConflictingGroupIds } from "../utils/validation";
import { waitlistCount } from "../utils/waitlist";
import { clinicCounts } from "../utils/membership";

export default function GroupsView({
  players, groups, coaches = [], movePlayer, updateGroup, removeFromGroup, deleteGroup, onWarnings,
  onUpdatePlayerTier, onWaitlistAdd, onWaitlistRemove, onWaitlistMove,
  onPlayerClick, onCreateGroup,
  filterTier, onFilterTierChange, onNotify, focusGroupId, onFocusHandled,
}) {
  const [search, setSearch] = useState("");
  const [dragOverGroupId, setDragOverGroupId] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  // The open group as it currently stands in the store. selectedGroup is only
  // a handle on which group is open; reading its fields would show them as
  // they were when the card was clicked.
  const openGroup = selectedGroup ? groups.find(g => g.id === selectedGroup.id) : null;

  // When the Issues tab jumps here, open the targeted group's modal, then
  // clear the request so it doesn't re-open on the next render.
  useEffect(() => {
    if (!focusGroupId) return;
    const g = groups.find(gr => gr.id === focusGroupId);
    if (g) setSelectedGroup(g);
    onFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusGroupId]);

  const playerMap = Object.fromEntries(players.map(p => [p.id, p]));
  const coachMap = Object.fromEntries(coaches.map(c => [c.id, c]));

  // Weekday instruction groups only — weekend open-play/private sessions live
  // on the Weekly Grid, not in this tier-organized list.
  const realGroups = groups.filter(g => g.tier !== "No Group" && !g.kind);
  const conflictIds = getConflictingGroupIds(groups);
  // One pass over the schedule so every card can say whether that member is
  // in more than one clinic without re-scanning the groups per card.
  const clinicCountByPlayer = clinicCounts(groups);

  const tierGroupCounts = {};
  TIER_ORDER.forEach(t => { tierGroupCounts[t] = 0; });
  realGroups.forEach(g => { if (tierGroupCounts[g.tier] !== undefined) tierGroupCounts[g.tier]++; });

  const filteredGroups = realGroups.filter(g => {
    const matchTier = filterTier === "All" || g.tier === filterTier;
    if (!matchTier) return false;
    if (!search) return true;
    return g.playerIds.some(pid => {
      const p = playerMap[pid];
      return p && p.name.toLowerCase().includes(search.toLowerCase());
    });
  });

  function handleAddToGroup(player) {
    // No optimistic edit of the snapshot: the panel renders from the live
    // group, and an assignment can now stop to ask whether it's a move or a
    // second clinic — showing them in the group before that is answered would
    // be showing something that hasn't happened.
    const target = openGroup || selectedGroup;
    const warnings = getWarnings(player, target);
    if (warnings.length > 0) onWarnings(warnings);
    movePlayer(player.id, player.groupId, target.id);
  }

  function handleRemovePlayer(player) {
    const groupId = selectedGroup.id;
    removeFromGroup(player.id, groupId);
    onNotify({
      message: `Removed ${player.name} from ${groupId}`,
      onUndo: () => movePlayer(player.id, null, groupId),
    });
  }

  function handleDrop(e, targetGroup) {
    e.preventDefault();
    e.stopPropagation();
    setDragOverGroupId(null);
    try {
      const { playerId, fromGroupId } = JSON.parse(e.dataTransfer.getData("text/plain"));
      if (fromGroupId === targetGroup.id) return;
      const player = playerMap[playerId];
      if (!player) return;
      const warnings = getWarnings(player, targetGroup);
      if (warnings.length > 0) onWarnings(warnings);
      movePlayer(playerId, fromGroupId, targetGroup.id);
    } catch {}
  }

  return (
    <div className="p-4">
      {/* Toolbar */}
      <div
        className="sticky z-30 bg-gray-50 flex gap-3 mb-4 flex-wrap items-center py-2 -mx-4 px-4 border-b border-gray-200"
        style={{ top: "var(--header-h, 0px)" }}
      >
        <input
          type="text"
          placeholder="Search by player name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        <select
          value={filterTier}
          onChange={e => onFilterTierChange(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <option value="All">All tiers</option>
          {TIER_ORDER.filter(t => t !== "No Group").map(t => (
            <option key={t} value={t}>{t} ({tierGroupCounts[t]})</option>
          ))}
        </select>
        <button
          onClick={() => onCreateGroup({})}
          className="sm:ml-auto flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
        >
          <span className="text-lg leading-none">+</span> New Group
        </button>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="text-amber-500">⚠</span> Not available at assigned time</span>
        <span className="flex items-center gap-1"><span className="text-purple-500">👥</span> Preferred partner not in same group</span>
        <span className="flex items-center gap-1"><span className="text-red-600">⚠</span> Group double-booked (court/time overlap)</span>
      </div>

      {/* Groups by tier */}
      <div className="space-y-8">
        {TIER_ORDER.filter(t => t !== "No Group").map(tier => {
          const tierGroups = filteredGroups.filter(g => g.tier === tier);
          if (tierGroups.length === 0) return null;
          const colors = TIER_COLORS[tier];
          return (
            <div key={tier}>
              <div className={`${colors.header} ${colors.text} px-3 py-1.5 rounded font-semibold text-sm mb-2 flex items-center justify-between`}>
                <span>{tier} — {tierGroups.length} group{tierGroups.length !== 1 ? "s" : ""}</span>
                <button
                  onClick={() => onCreateGroup({ tier })}
                  className="text-xs font-medium opacity-70 hover:opacity-100 transition-opacity"
                  title={`New ${tier} group`}
                >
                  + New
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {tierGroups.map(group => {
                  const count = group.playerIds.length;
                  const isOver = dragOverGroupId === group.id;
                  const { startTime } = getGroupSchedule(group);
                  return (
                    <div
                      key={group.id}
                      onDrop={e => handleDrop(e, group)}
                      onDragOver={e => { e.preventDefault(); setDragOverGroupId(group.id); }}
                      onDragLeave={() => setDragOverGroupId(null)}
                      onClick={() => setSelectedGroup(group)}
                      className={`
                        ${colors.bg} ${colors.border} border rounded-lg p-3 transition-all cursor-pointer
                        ${isOver ? "ring-2 ring-blue-400 shadow-md" : "hover:brightness-95"}
                      `}
                    >
                      {/* Card header */}
                      <div className="flex justify-between items-center mb-2">
                        <span className={`font-mono text-xs font-bold ${colors.text} flex items-center gap-1`}>
                          {group.id}
                          {group.notes && (
                            <span title="This group has notes">📝</span>
                          )}
                        </span>
                        {/* Segmented fill bar */}
                        <div className="flex items-center gap-1">
                          <div className="flex gap-0.5">
                            {[0, 1, 2, 3].map(i => (
                              <div
                                key={i}
                                className={`h-2 w-3 rounded-sm ${i < count ? "bg-green-500" : "bg-white bg-opacity-60 border border-gray-200"}`}
                              />
                            ))}
                          </div>
                          <span className={`text-xs font-semibold ${count >= 4 ? "text-green-700" : "text-gray-500"}`}>
                            {count}/4
                          </span>
                          {waitlistCount(group) > 0 && (
                            <span
                              className="text-xs font-semibold text-slate-600"
                              title={`${waitlistCount(group)} waiting to join`}
                            >
                              ⏳{waitlistCount(group)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Schedule info */}
                      <div className="text-xs text-gray-500 mb-2 leading-tight">
                        {group.day && startTime ? (
                          <>{group.day} · {startTime}–{getEndTime(group)}<br />{group.court}</>
                        ) : (
                          <span className="italic">Unscheduled</span>
                        )}
                        {conflictIds.has(group.id) && (
                          <div className="mt-1 inline-flex items-center gap-1 text-red-600 font-semibold" title="This court/time overlaps another group">
                            ⚠ Double-booked
                          </div>
                        )}
                        <div className="mt-0.5">
                          {group.coachId && coachMap[group.coachId] ? (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: coachMap[group.coachId].color || "#64748b" }} />
                              {coachMap[group.coachId].name}
                            </span>
                          ) : (
                            <span className="italic text-gray-400">No coach</span>
                          )}
                        </div>
                      </div>

                      {/* Players */}
                      <div className="space-y-1.5 min-h-[2rem]">
                        {count === 0 ? (
                          <div className="border-2 border-dashed border-gray-300 border-opacity-60 rounded-md py-3 text-center text-gray-400 text-xs italic">
                            Drop a player here
                          </div>
                        ) : (
                          group.playerIds.map(pid => {
                            const p = playerMap[pid];
                            return p ? (
                              <PlayerCard
                                key={pid}
                                player={p}
                                draggable
                                contextGroupId={group.id}
                                clinicCount={clinicCountByPlayer[pid] || 1}
                                warn={hasAvailabilityConflict(p, group)}
                                partnerFlag={hasUnmetPartnerPreference(p, players)}
                                onClick={(e) => { e.stopPropagation(); onPlayerClick(p); }}
                              />
                            ) : null;
                          })
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {selectedGroup && openGroup && (
        <GroupModal
          // Keyed so switching to another group can't carry over the previous
          // one's unsaved notes/schedule drafts.
          key={selectedGroup.id}
          // Read the live record rather than the snapshot taken when the card
          // was clicked: waitlist edits go straight to the store, and a stale
          // copy would leave the panel showing the queue as it was on open.
          group={openGroup}
          groupPlayers={openGroup.playerIds.map(id => playerMap[id]).filter(Boolean)}
          allPlayers={players}
          allGroups={groups}
          coaches={coaches}
          onClose={() => setSelectedGroup(null)}
          onPlayerClick={onPlayerClick}
          onAddPlayer={handleAddToGroup}
          onRemovePlayer={handleRemovePlayer}
          onUpdatePlayerTier={onUpdatePlayerTier}
          onWaitlistAdd={onWaitlistAdd}
          onWaitlistRemove={onWaitlistRemove}
          onWaitlistMove={onWaitlistMove}
          onDeleteGroup={groupId => { deleteGroup(groupId); setSelectedGroup(null); }}
          onUpdateGroup={(groupId, updates) => {
            updateGroup(groupId, updates);
            setSelectedGroup(prev => (prev && prev.id === groupId ? { ...prev, ...updates } : prev));
          }}
        />
      )}
    </div>
  );
}
